import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api, setAuthHeader } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const CONFIRM_PHRASE = 'DELETE';

export default function DeleteAccountDialog({ open, onOpenChange }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [confirmText, setConfirmText] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const canDelete = confirmText.trim().toUpperCase() === CONFIRM_PHRASE;

    const submit = async () => {
        if (!canDelete || submitting) return;
        setSubmitting(true);
        try {
            await api.delete('/auth/me');
            setAuthHeader(null);
            // Clear local prefs (cookie consent etc.)
            try {
                localStorage.removeItem('ns_session_token');
            } catch { /* ignore */ }
            // Force-reset auth context user too, by calling logout (which clears the user)
            try { await logout(); } catch { /* server may already be done */ }
            toast.success('Your account and all associated data have been deleted.');
            onOpenChange(false);
            navigate('/login', { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not delete your account');
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!submitting) onOpenChange(o); }}>
            <DialogContent
                data-testid="delete-account-dialog"
                className="sm:max-w-md rounded-3xl border-[var(--ns-border)]"
            >
                <DialogHeader>
                    <div
                        className="mx-auto w-12 h-12 rounded-2xl flex items-center justify-center mb-3"
                        style={{ background: 'rgba(208,92,69,0.10)' }}
                    >
                        <AlertTriangle className="text-[#D05C45]" />
                    </div>
                    <DialogTitle className="text-center font-display text-2xl tracking-tight">
                        Delete your account?
                    </DialogTitle>
                </DialogHeader>

                <p className="text-sm text-[var(--ns-text2)] leading-relaxed text-center">
                    This will <span className="font-semibold text-[var(--ns-text)]">permanently delete</span> your account
                    ({user?.email}), all your trips, saved ideas, preferences, and uploaded data. This cannot be undone.
                </p>

                <div className="rounded-2xl border border-[var(--ns-border)] bg-[var(--ns-soft)] p-4 mt-2 text-sm text-[var(--ns-text2)] space-y-1">
                    <div>• Your user record and session will be deleted.</div>
                    <div>• Trips where you're the only participant will be removed entirely.</div>
                    <div>• Group trips will keep going — you'll just be removed from them.</div>
                    <div>• Your preferences, votes, personality, budget, and saved ideas will be wiped.</div>
                </div>

                <div className="mt-2">
                    <label htmlFor="delete-confirm-input" className="text-sm text-[var(--ns-text2)]">
                        Type <span className="font-mono font-semibold text-[var(--ns-text)]">{CONFIRM_PHRASE}</span> to confirm
                    </label>
                    <Input
                        id="delete-confirm-input"
                        autoFocus
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder={CONFIRM_PHRASE}
                        data-testid="delete-account-confirm-input"
                        className="rounded-xl border-[var(--ns-border)] bg-white h-11 mt-1.5"
                    />
                </div>

                <div className="flex items-center gap-2 mt-2">
                    <Button
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        disabled={submitting}
                        data-testid="delete-account-cancel-button"
                        className="rounded-full h-11 flex-1"
                    >
                        Keep my account
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={!canDelete || submitting}
                        data-testid="delete-account-confirm-button"
                        className="rounded-full h-11 flex-1 text-white disabled:opacity-50"
                        style={{ background: '#D05C45' }}
                    >
                        {submitting ? <><Loader2 size={14} className="mr-2 animate-spin" /> Deleting…</> : <><Trash2 size={14} className="mr-2" /> Delete forever</>}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
